import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  OnDestroy,
} from '@angular/core';
import { Dialog } from 'primeng/dialog';

import { FormsModule } from '@angular/forms';
import { QuotesService } from '../../../quotes.service';
import { QuoteService } from '../../../quote.service';
import {
  isInterceptorHandledError,
  parseApiError,
} from '../../../quotes-api-error';
import { Subscription } from 'rxjs';
import { QuoteItemDataEditorService } from './quote-item-data-editor/quote-item-data-editor.service';
import { QuoteItemDataEditorComponent } from './quote-item-data-editor/quote-item-data-editor.component';
import { Blueprint } from '../../../../models/blueprint.model';
import { Button } from 'primeng/button';
import { Message } from 'primeng/message';
import { EquipmentService } from '../../../equipment.service';
import { Equipment } from '../../../../models/equipment.model';
import { ConsumableService } from '../../../consumable.service';
import { Consumable } from '../../../../models/consumable.model';
import { QuoteRevisionItemWrite } from '../../../../models/quote-revision-item.model';
import { quantityInputToDecimal } from '../../../../models/quote-item-data.model';
import { QuoteRevisionItemService } from '../../../services/quote-revision-item.service';

@Component({
  selector: 'app-quote-revision-item-add',
  imports: [Button, Dialog, FormsModule, Message, QuoteItemDataEditorComponent],
  providers: [QuoteItemDataEditorService],
  templateUrl: './quote-revision-item-add.component.html',
  styleUrl: './quote-revision-item-add.component.scss',
})
export class QuoteRevisionItemAddComponent implements OnDestroy {
  quotesService: QuotesService = inject(QuotesService);
  quoteService: QuoteService = inject(QuoteService);
  revisionItemService = inject(QuoteRevisionItemService);
  equipmentService = inject(EquipmentService);
  consumableService = inject(ConsumableService);
  quoteItemDataEditorService: QuoteItemDataEditorService = inject(
    QuoteItemDataEditorService,
  );
  isSubmitting = false;
  submitError: string | null = null;

  @Input()
  parentQuoteItemPersistentId: string | null = null;

  @Input()
  currencyCode?: string;

  @Input()
  visible: boolean = false;

  @Input()
  tradeStudyToolFeaturesEnabled: boolean = false;

  @Input()
  consumablesEnabled: boolean = false;

  @Input()
  pdfDrawingUploadEnabled: boolean = false;

  @Output()
  visibleChange = new EventEmitter<boolean>();

  // Blueprints for the item tree editor
  allBlueprints: Blueprint[] = [];
  equipmentOptions: Equipment[] = [];
  consumableOptions: Consumable[] = [];

  // Track subscriptions for cleanup
  private subscriptions = new Subscription();

  /**
   * Initialize the dialog when it becomes visible.
   * Called by p-dialog's (onShow) event.
   */
  onDialogShow(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.loadBlueprints();
    this.loadEquipment();
    this.loadConsumables();
  }

  onVisibleChange(nextVisible: boolean): void {
    this.visible = nextVisible;

    // Required to propagate the change to the parent component
    this.visibleChange.emit(nextVisible);
  }

  private loadBlueprints(): void {
    const subscription = this.quotesService.getBlueprints$().subscribe({
      next: (blueprints: Blueprint[]) => {
        this.allBlueprints = blueprints;
      },
      error: (error: unknown) => {
        console.error('Error loading blueprints:', error);
      },
    });
    this.subscriptions.add(subscription);
  }

  private loadEquipment(): void {
    if (!this.tradeStudyToolFeaturesEnabled) {
      this.equipmentOptions = [];
      return;
    }

    const subscription = this.equipmentService.activeEquipment$.subscribe({
      next: (equipment: Equipment[]) => {
        this.equipmentOptions = equipment;
      },
      error: (error: unknown) => {
        console.error('Error loading equipment:', error);
      },
    });
    this.subscriptions.add(subscription);
    void this.equipmentService.loadEquipment();
  }

  private loadConsumables(): void {
    if (!this.consumablesEnabled) {
      this.consumableOptions = [];
      return;
    }

    const subscription = this.consumableService.activeConsumables$.subscribe({
      next: (consumables: Consumable[]) => {
        this.consumableOptions = consumables;
      },
      error: (error: unknown) => {
        console.error('Error loading consumables:', error);
      },
    });
    this.subscriptions.add(subscription);
    void this.consumableService.loadConsumables();
  }

  /**
   * Clean up state when the dialog is closed.
   * Called by p-dialog's (onHide) event.
   */
  closeDialog(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.quoteItemDataEditorService.clearItems();
    this.submitError = null;
    this.onVisibleChange(false);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Submits the quote item data tree through the revision-item bulk API.
   */
  async onSubmitItemTree(): Promise<void> {
    const itemsData = this.quoteItemDataEditorService.itemsForSubmit();
    if (itemsData.length === 0) {
      return;
    }

    const parentId = this.parentQuoteItemPersistentId;
    this.isSubmitting = true;
    this.submitError = null;
    try {
      await this.revisionItemService.bulkCreate(
        this.toRevisionItemWrites(itemsData, parentId),
      );
      this.closeDialog();
    } catch (error) {
      this.handleSubmitError(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  get dialogCurrencyCode(): string {
    return this.currencyCode ?? this.quoteService.revision.currency_code;
  }

  private toRevisionItemWrites(
    items: ReturnType<QuoteItemDataEditorService['itemsForSubmit']>,
    selectedParentPersistentId: string | null,
  ): QuoteRevisionItemWrite[] {
    // The editor uses temporary `local-*` IDs before persistence. Revision
    // items require UUID identities, so translate every temporary identity
    // together with its parent reference.
    const persistentIdByEditorId = new Map(
      items.map((item) => [item.persistent_id, crypto.randomUUID()]),
    );
    return items.map((item) => ({
      persistent_id: persistentIdByEditorId.get(item.persistent_id)!,
      parent_persistent_id: item.parent_id
        ? persistentIdByEditorId.get(item.parent_id)!
        : selectedParentPersistentId,
      sibling_order: (item.sibling_order + 1) * 100,
      name: item.name,
      quantity: quantityInputToDecimal(item.quantity, item.quantity_type),
      quantity_type: item.quantity_type,
      unit_cost: item.unit_cost,
      batch_size: item.batch_size,
      markup_percentage: item.markup_percentage,
      tax_percentage: item.tax_percentage,
      pricing_model_id: item.pricing_model_id ?? null,
      material_id: item.material_id ?? null,
      inherit_material: item.inherit_material ?? false,
      pricing_parameters: item.pricing_parameters,
      cad_object_ids: item.cad_object_ids,
      drawing_file_ids: item.drawing_file_id ? [item.drawing_file_id] : [],
      equipment_links: (item.capex_equipment_ids ?? []).map((equipment_id) => ({
        equipment_id,
      })),
      ...(this.consumablesEnabled
        ? { consumable_links: item.consumables ?? [] }
        : {}),
    }));
  }

  private handleSubmitError(error: unknown): void {
    if (isInterceptorHandledError(error)) {
      return;
    }
    this.submitError = parseApiError(
      error,
      'An error occurred while adding items to the quote.',
    );
    console.error('Unable to add quote items:', error);
  }
}
